import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { AlertService } from 'src/app/services/auth';
import { MenuService } from 'src/app/services/menu/menu-service';
import { SyncService } from 'src/app/services/offline/sync.service';
import { Order } from 'src/app/shared/models/orders';
import { HormigonDashboardFacade } from '../../dashboard/hormigon-dashboard.facade';
import { HormigonDashboardRow } from '../../dashboard/hormigon-dashboard.types';
import { HormigonDispatchFacade } from '../hormigon-dispatch.facade';
import {
  HormigonDispatchSelectors,
  selectCurrentHormigonDispatch,
} from '../hormigon-dispatch-selector';

@Component({
    selector: 'app-hormigon-pedido-pendiente',
    templateUrl: './pedido-pendiente.component.html',
    styleUrls: ['./pedido-pendiente.component.css'],
    standalone: false
})
export class HormigonPedidoPendienteComponent implements OnInit, OnDestroy {
  TextoInfo: string = 'Ver más información';
  currentOrder: Order | null = null;
  currentConflicts: HormigonDashboardRow[] = [];
  /** True solo cuando el resumen de hoy y, si aplica, el detalle del ticket ya terminaron. */
  dispatchResolved = false;
  private destroy$ = new Subject<void>();
  private readonly loadCurrent$ = new Subject<boolean>();
  private loadGeneration = 0;
  private lastConflictSignature = '';

  private readonly dispatchSelectors: HormigonDispatchSelectors<HormigonDashboardRow> = {
    deliveryDate: (row) => row.deliveryDate,
    status: (row) => row.status,
    flagPending: (row) => row.flagPending,
    startTime: (row) => row.deliveryTime,
    identifier: (row) => row.ticket,
  };

  constructor(
    private menu: MenuService,
    private readonly dispatchFacade: HormigonDispatchFacade,
    private readonly dashboardFacade: HormigonDashboardFacade,
    private syncService: SyncService,
    private readonly alertService: AlertService,
    private readonly router: Router,
  ) {
    menu.btnPrevius = true;
    menu.tilteMenu = 'Despacho actual';

  }

  onContinue(order: Order): void {
    this.router.navigate(['/pedidospendiente/detalle'], {
      queryParams: { tkt: order.tkt, paso: 'observacion' },
    });
  }

  onPaperGuide(order: Order): void {
    this.router.navigate(['/pedidospendiente/detalle'], {
      queryParams: { tkt: order.tkt, mode: 'papel' },
    });
  }

  isAccordionOpen = false;

  toggleAccordion() {
    this.isAccordionOpen = !this.isAccordionOpen;
    if (this.isAccordionOpen) {
      this.TextoInfo = 'Ver menos información';
    } else {
      this.TextoInfo = 'Ver más información';
    }
  }

  ngOnInit(): void {
    this.loadCurrent$
      .pipe(
        debounceTime(80),
        switchMap((forceNetwork) => this.loadCurrentDispatch(forceNetwork)),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.loadCurrent$.next(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCurrentDispatch(forceNetwork: boolean): Observable<void> {
    const generation = ++this.loadGeneration;
    this.dispatchResolved = false;
    const hoy = moment().format('YYYY-MM-DD');
    return this.dashboardFacade.loadTodayCandidates(hoy).pipe(
      catchError((err) => {
        console.error('Error al obtener despacho actual:', err);
        return EMPTY;
      }),
      switchMap((rows) => this.fetchWinner(rows || [], forceNetwork)),
      finalize(() => {
        if (generation === this.loadGeneration) {
          this.dispatchResolved = true;
        }
      }),
    );
  }

  private fetchWinner(
    rows: HormigonDashboardRow[],
    forceNetwork: boolean,
  ): Observable<void> {
    const selection = selectCurrentHormigonDispatch(
      rows,
      this.dispatchSelectors,
      (row) => row.guideState === 'signed',
    );
    this.currentConflicts = selection.currentConflicts;
    this.notifyCurrentConflicts(selection.currentConflicts);

    const ticket = selection.current?.ticket;
    if (!ticket) {
      this.currentOrder = null;
      return of(undefined);
    }

    const refresh = forceNetwork && this.dashboardFacade.isOnline();
    return this.dispatchFacade.getByTicket(ticket, true, refresh).pipe(
      map((orders) => {
        this.currentOrder = orders?.[0] ?? null;
      }),
      catchError((err) => {
        console.error('Error al obtener despacho actual:', err);
        this.currentOrder = null;
        return of(undefined);
      }),
    );
  }

  private notifyCurrentConflicts(conflicts: HormigonDashboardRow[]): void {
    if (!conflicts.length) {
      this.lastConflictSignature = '';
      return;
    }

    const signature = conflicts
      .map((row) => row.ticket)
      .sort()
      .join(',');
    if (signature === this.lastConflictSignature) return;

    this.lastConflictSignature = signature;
    this.alertService.warn(
      `Se detectaron varios viajes en curso para hoy. Tickets adicionales: ${signature}.`,
    );
  }

  abrirEnGoogleMaps(direccion: string): void {
    const dir = direccion.replace('.', ' ');
    const query = encodeURIComponent(dir);
    window.location.href = `https://www.google.com/maps/search/?api=1&query=${query}`;
  }

  abrirEnWaze(direccion: string): void {
    const dir = direccion.replace('.', ' ');
    const query = encodeURIComponent(dir);
    window.location.href = `https://waze.com/ul?q=${query}&navigate=yes`;
  }

  updateUrl(): void {
    this.syncService.flushNow();
    this.loadCurrent$.next(true);
  }


  // abrirModalConfirmacion() {
  //   const dialogRef = this.dialog.open(ModalConfirmarDatosComponent, {
  //     width: '400px',
  //     disableClose: true
  //   });

  //   dialogRef.afterClosed().subscribe((resultado) => {
  //     if (resultado) {
  //       console.log('✅ Usuario confirmó');
  //     } else {
  //       console.log('❌ Usuario canceló');
  //     }
  //   });
  // }
}
